import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiFacebook, FiInstagram } from 'react-icons/fi';
import { FaWhatsapp, FaXTwitter } from 'react-icons/fa6';
import { useMediaLinks } from '../hooks/useMediaLinks';
import { useLanguage } from '../context/LanguageContext';

const FooterContainer = styled.footer`
  background: #5efba9;
  color: #1f2937;
  padding: 32px 0;
  margin-top: 64px;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FooterTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }
`;

const Copyright = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  margin: 0;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const SocialIcon = styled.a`
  color: #1f2937;
  font-size: 24px;
  transition: transform 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    transform: scale(1.1);
  }

  ${props => props.$disabled && `
    opacity: 0.4;
    pointer-events: none;
    transform: none;
  `}
`;

const NavigationLinks = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FooterLink = styled(Link)`
  color: #1f2937;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

function Footer() {
  const { links, loading } = useMediaLinks();
  const { isArabic } = useLanguage();


  const socialLinks = [
    { href: links?.whatsapp_url, label: 'WhatsApp', icon: <FaWhatsapp /> },
    { href: links?.instagram_url, label: 'Instagram', icon: <FiInstagram /> },
    { href: links?.x_url, label: 'X (Twitter)', icon: <FaXTwitter /> },
    { href: links?.facebook_url, label: 'Facebook', icon: <FiFacebook /> },
  ].filter(link => !!link.href);

  return (
    <FooterContainer>
      <FooterContent>
        <FooterTop>
          <LeftSection>
            <Copyright>
              {isArabic ? '©2025 WASL. جميع الحقوق محفوظة' : '©2025 WASL. All Rights Reserved'}
            </Copyright>
            <SocialLinks>
              {loading && socialLinks.length === 0 ? (
                <span style={{ fontSize: '14px', color: '#4b5563' }}>Loading...</span>
              ) : socialLinks.length > 0 ? (
                socialLinks.map(({ href, label, icon }) => (
                  <SocialIcon
                    key={label}
                    href={href ?? '#'}
                    aria-label={label}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {icon}
                  </SocialIcon>
                ))
              ) : (
                <span style={{ fontSize: '14px', color: '#4b5563' }}>Find us on social media soon</span>
              )}
            </SocialLinks>
          </LeftSection>

          <NavigationLinks>
            <FooterLink to="/sell-with-us">
              {isArabic ? 'البيع معنا' : 'Sell With Us'}
            </FooterLink>
            <FooterLink to="/contact">
              {isArabic ? 'اتصل بنا' : 'Contact Us'}
            </FooterLink>
            <FooterLink to="/privacy-policy">
              {isArabic ? 'سياسة الخصوصية' : 'Privacy Policy'}
            </FooterLink>
            <FooterLink to="/terms-and-conditions">
              {isArabic ? 'الشروط والأحكام' : 'Terms and Conditions'}
            </FooterLink>
            <FooterLink to="/faq">
              {isArabic ? 'الأسئلة الشائعة' : 'FAQ'}
            </FooterLink>
          </NavigationLinks>
        </FooterTop>
      </FooterContent>
    </FooterContainer>
  );
}

export default Footer;
